import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Search, Grid3X3, List, BarChart3, Plus,
  Eye, Edit, Clock, AlertCircle, 
  CheckCircle, DollarSign,
  FileText, BookOpen, Calculator, Archive, RefreshCw, Hourglass,
  CheckSquare
} from 'lucide-react';
import { formatearFechaCompleta} from '../../../utils/utils';
import NuevaSolicitud from './NuevaSolicitud';
import CardSolicitud from '../../../views/compartedRoutes/solicitudes/CardSolicitud';

const DashboardSolicitudes = ({ 
  solicitudes = [], 
  loading = false, 
  onRefrescar,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vista, setVista] = useState(searchParams.get('view') || 'cards');
  const [filtros, setFiltros] = useState({
    busqueda: searchParams.get('search') || '',
    fecha: searchParams.get('fecha') || 'todas',
    estado: searchParams.get('estado') || 'todos',
    servicio: searchParams.get('servicio') || 'todos',
    disponibilidad: searchParams.get('disponibilidad') || 'todos'
  });
  const [menuAbierto, setMenuAbierto] = useState(null);
  const [modalNuevaSolicitudAbierto, setModalNuevaSolicitudAbierto] = useState(false);
  const [creandoSolicitud, setCreandoSolicitud] = useState(false);

  // Sincronizar filtros con URL cuando cambian los searchParams
  useEffect(() => {
    setFiltros({
      busqueda: searchParams.get('search') || '',
      fecha: searchParams.get('fecha') || 'todas',
      estado: searchParams.get('estado') || 'todos',
      servicio: searchParams.get('servicio') || 'todos',
      disponibilidad: searchParams.get('disponibilidad') || 'todos' //cambiar a disponibles
    });
    setVista(searchParams.get('view') || 'cards');
  }, [searchParams]);

  // Función para actualizar los parámetros de URL
  const actualizarParametrosURL = (nuevosFiltros, nuevaVista = null) => {
    const params = new URLSearchParams();
    
    // Agregar vista si se especifica
    if (nuevaVista) {
      params.set('view', nuevaVista);
    } else if (vista) {
      params.set('view', vista);
    }
    
    // Agregar filtros solo si no son los valores por defecto
    if (nuevosFiltros.busqueda && nuevosFiltros.busqueda.trim() !== '') {
      params.set('search', nuevosFiltros.busqueda);
    }
    
    if (nuevosFiltros.disponibilidad && nuevosFiltros.disponibilidad !== 'todos') {
      params.set('disponibilidad', nuevosFiltros.disponibilidad);
    }
    
    if (nuevosFiltros.fecha && nuevosFiltros.fecha !== 'todas') {
      params.set('fecha', nuevosFiltros.fecha);
    }
    
    if (nuevosFiltros.estado && nuevosFiltros.estado !== 'todos') {
      params.set('estado', nuevosFiltros.estado);
    }
    
    if (nuevosFiltros.servicio && nuevosFiltros.servicio !== 'todos') {
      params.set('servicio', nuevosFiltros.servicio);
    }
    
    setSearchParams(params);
  };

  // Función para cambiar filtros y actualizar URL
  const cambiarFiltros = (nuevosFiltros) => {
    const filtrosActualizados = { ...filtros, ...nuevosFiltros };
    setFiltros(filtrosActualizados);
    actualizarParametrosURL(filtrosActualizados);
  };

  // Función para cambiar vista y actualizar URL
  const cambiarVista = (nuevaVista) => {
    setVista(nuevaVista);
    actualizarParametrosURL(filtros, nuevaVista);
  };

  // Función para limpiar todos los filtros
  const limpiarFiltros = () => {
    const filtrosVacios = {
      busqueda: '',
      fecha: 'todas',
      estado: 'todos',
      servicio: 'todos',
      disponibilidad: 'todos'
    };
    setFiltros(filtrosVacios);
    
    // Solo mantener la vista en la URL
    const params = new URLSearchParams();
    if (vista) {
      params.set('view', vista);
    }
    setSearchParams(params);
  };

  //Filtro por estado, Disponibles - A pasar precio - Esperando
  const obtenerEstadoDisponibilidad = (solicitud) => {
    const estadoLower = solicitud.Estado;
    
    // Veiricar cotizaciones
    const tieneCotizacionesConPrecio = solicitud.cotizaciones && 
      solicitud.cotizaciones.some(cotizacion => {
        const precio = cotizacion.cotizacion || 0;
        return precio > 0;
      });
    
      if (estadoLower == "ESPERANDO") {
        return 'esperando';
      }

      if(estadoLower === "DISPONIBLE"){

        if(tieneCotizacionesConPrecio){
          return 'a_pasar_precio';
        }

        return 'disponible';

      }

      if(estadoLower === "A PAGAR"){
        return 'a_pagar'
      }

      // Por defecto, considerar disponible
      return 'disponible';
  };

  // Solicitudes por 3 categorias
  const solicitudesDisponibles = solicitudes.filter(s => obtenerEstadoDisponibilidad(s) === 'disponible');
  const solicitudesDisponiblesYPasarPrecio = solicitudes.filter(s => s.Estado === "DISPONIBLE");
  const solicitudesAPasarPrecio = solicitudes.filter(s => obtenerEstadoDisponibilidad(s) === 'a_pasar_precio');
  const solicitudesEsperando = solicitudes.filter(s => obtenerEstadoDisponibilidad(s) === 'esperando');
  const solicitudesAPagar = solicitudes.filter(s => s.Estado === "A PAGAR");

  //Colores para servicios, icono y label
  const obtenerInfoServicio = (servicio) => {
    const servicioLower = servicio;
    
    const mapeoServicios = {
      'PARCIAL': { 
        icon: <FileText size={20} />, 
        color: 'from-green-500 to-green-600', 
        label: 'Parcial' },
      'TALLER': { 
        icon: <FileText size={20} />, 
        color: 'from-blue-500 to-blue-600', 
        label: 'Taller' },
      'TESIS': { 
        icon: <BookOpen size={20} />, 
        color: 'from-purple-500 to-purple-600', 
        label: 'Tesis' },
      'QUIZ': { 
        icon: <Calculator size={20} />, 
        color: 'from-green-500 to-green-600', 
        label: 'Quiz' },
      'CLASES PERSONALIZADAS': { 
        icon: <Calculator size={20} />, 
        color: 'from-green-500 to-green-600', 
        label: 'Clase personalizada' },
    };

    return mapeoServicios[servicioLower] || { 
      icon: <FileText size={20} />, 
      color: 'from-gray-500 to-gray-600', 
      label: servicio 
    };
  };

  //Prioridad de trabajo
  const calcularPrioridad = (fechaEntrega) => {
    const ahora = new Date();
    const entrega = new Date(fechaEntrega);
    const horasRestantes = (entrega - ahora) / (1000 * 60 * 60);

    if (horasRestantes < 0) return { 
      tipo: 'vencida', 
      label: 'Vencida', 
      color: 'from-gray-500 to-gray-600' };
    if (horasRestantes <= 24) return { 
      tipo: 'urgente', 
      label: '< 24hr', 
      color: 'from-red-500 to-red-600' };
    if (horasRestantes <= 36) return { 
      tipo: 'medio', 
      label: '< dia y medio', 
      color: 'from-yellow-500 to-orange-500' };
    if (horasRestantes <= 48) return { 
      tipo: 'normal', 
      label: '< 2 dias', 
      color: 'from-green-500 to-green-600' };
    return { 
      tipo: 'normal', 
      label: `${Math.ceil(horasRestantes / 24)} días`, 
      color: 'from-green-500 to-green-600' };
  };

  // Calcular estadisticas segun filtro de disponibilidad
  const calcularStats = () => {
    let solicitudesParaStats = [];
    
    if (filtros.disponibilidad === 'disponibles') {
      solicitudesParaStats = solicitudesDisponibles;
    } else if (filtros.disponibilidad === 'a_pasar_precio') {
      solicitudesParaStats = solicitudesAPasarPrecio;
    } else if (filtros.disponibilidad === 'esperando') {
      solicitudesParaStats = solicitudesEsperando;
    } else {
      solicitudesParaStats = solicitudes;
    }

    const urgentes = solicitudesParaStats.filter(s => calcularPrioridad(s.fechaentrega).tipo === 'urgente').length;
    const medias = solicitudesParaStats.filter(s => calcularPrioridad(s.fechaentrega).tipo === 'medio').length;
    const normales = solicitudesParaStats.filter(s => calcularPrioridad(s.fechaentrega).tipo === 'normal').length;
    const total = solicitudesParaStats.length;

    // Estadísticas específicas según disponibilidad
    if (filtros.disponibilidad === 'disponibles') {
      return {
        urgentes,
        medias,
        normales,
        total,
        label1: 'Para Cotizar',
        totalLabel: 'Disponibles'
      };
    } else if (filtros.disponibilidad === 'a_pasar_precio') {
      return {
        urgentes,
        medias,
        normales,
        total,
        label1: 'Con Cotizaciones',
        totalLabel: 'A Pasar Precio'
      };
    } else if (filtros.disponibilidad === 'esperando') {
      return {
        urgentes,
        medias,
        normales,
        total,
        label1: 'En Proceso',
        totalLabel: 'Esperando'
      };
    } else {
      return {
        urgentes,
        medias,
        normales,
        total,
        label1: 'Total General',
        totalLabel: 'Todas'
      };
    }
  };
  const stats = calcularStats();

  // Filtrar solicitudes - revisar mas
  const solicitudesFiltradas = (() => {
    let baseSolicitudes = solicitudes;
    
    // Filtro por disponibilidad (este es exclusivo)
    if (filtros.disponibilidad === 'disponibles') {
      baseSolicitudes = solicitudesDisponibles;
    } else if (filtros.disponibilidad === 'a_pasar_precio') {
      baseSolicitudes = solicitudesAPasarPrecio;
    } else if (filtros.disponibilidad === 'esperando') {
      baseSolicitudes = solicitudesEsperando;
    } else if (filtros.disponibilidad === 'disponible_pasar_precio'){
      baseSolicitudes = solicitudesDisponiblesYPasarPrecio;
    } else if (filtros.disponibilidad === 'a_pagar'){
      baseSolicitudes = solicitudesAPagar;
    }
    
    const filtradas = baseSolicitudes.filter(solicitud => {

      const cumpleBusqueda = 
        solicitud.id.toString().toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
        //solicitud.Servicio.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
        solicitud.materia.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
        solicitud.cliente.toString().toLowerCase().includes(filtros.busqueda.toLowerCase());
        //solicitud.resumen.toLowerCase().includes(filtros.busqueda.toLowerCase());
      
      const cumpleFecha = filtros.fecha === 'todas' || 
                         (filtros.fecha === 'hoy' && new Date(solicitud.fechaentrega).toDateString() === new Date().toDateString());
      
      const cumpleEstado = filtros.estado === 'todos' || solicitud.Estado.toLowerCase() === filtros.estado;
      
      const cumpleServicio = filtros.servicio === 'todos' || solicitud.servicio.toLowerCase() === filtros.servicio;

      return cumpleBusqueda && cumpleFecha && cumpleEstado && cumpleServicio;
    });

    return filtradas.sort((a, b) => a.id - b.id);
    
  })();

  const obtenerBadgePrioridad = (fechaEntrega) => {
    const prioridad = calcularPrioridad(fechaEntrega);
    
    const estilos = {
      urgente: 'bg-red-100 text-red-800 border-red-200',
      medio: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      normal: 'bg-green-100 text-green-800 border-green-200',
      vencida: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    const iconos = {
      urgente: '🚨',
      medio: '⏰',
      normal: '✅',
      vencida: '⏰'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${estilos[prioridad.tipo]}`}>
        {iconos[prioridad.tipo]} {prioridad.label}
      </span>
    );
  };

  const obtenerBadgeDisponibilidad = (solicitud) => {
    const estado = obtenerEstadoDisponibilidad(solicitud);
    
    if (estado === 'disponible') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
          <CheckSquare size={12} className="mr-1" />
          Disponible
        </span>
      );
    } else if (estado === 'a_pasar_precio') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200">
          <DollarSign size={12} className="mr-1" />
          A Pasar Precio
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
          <Hourglass size={12} className="mr-1" />
          Esperando
        </span>
      );
    }
  };

  // Cargando solciitudes
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Cargando solicitudes...</p>
        </div>
      </div>
    );
  }

  //Crear nueva solciitud
  const handleCrearSolicitud = () =>{
    setModalNuevaSolicitudAbierto(true);
  }

  //cerrar solicitud
  const handleCerrarSolicitud = () =>{
    setModalNuevaSolicitudAbierto(false);
  }

  //calcular tiempo
  const calcularTiempoPublicado = (fechaPublicacion) =>{
    const ahora = new Date();
    const publicacion = new Date(fechaPublicacion);
    const tiempoTranscurrido = ahora - publicacion;

     const dias = Math.floor(tiempoTranscurrido / (1000 * 60 * 60 * 24));
    const horas = Math.floor((tiempoTranscurrido % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutos = Math.floor((tiempoTranscurrido % (1000 * 60 * 60)) / (1000 * 60));
    
    return { dias, horas, minutos, totalHoras: tiempoTranscurrido / (1000 * 60 * 60) };
  }
  
  // Función para mensaje vacío por columna
  const mensajeColumnaVacia = (tipo, icono, mensaje) => (
    <div className="flex items-center justify-center h-32 text-gray-500">
      <div className="text-center">
        <div className="text-4xl mb-2">{icono}</div>
        <div className="text-sm">{mensaje}</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">

      {/* Header y Controles Todo Compacto */}
      {solicitudes.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          {/* Primera fila: Título, botones y estadísticas mini */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            {/* Lado izquierdo: Título y estadísticas mini */}
            <div className="flex items-center gap-6">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Gestión de Solicitudes</h1>
                <p className="text-xs text-gray-500">Vista completa del sistema</p>
              </div>
              
              {/* Estadísticas mini */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 bg-red-50 px-2 py-1 rounded">
                  <AlertCircle size={14} className="text-red-600" />
                  <span className="text-xs font-medium text-red-700">{stats.urgentes}</span>
                </div>
                
                <div className="flex items-center gap-1.5 bg-yellow-50 px-2 py-1 rounded">
                  <Clock size={14} className="text-yellow-600" />
                  <span className="text-xs font-medium text-yellow-700">{stats.medias}</span>
                </div>
                
                <div className="flex items-center gap-1.5 bg-green-50 px-2 py-1 rounded">
                  <CheckCircle size={14} className="text-green-600" />
                  <span className="text-xs font-medium text-green-700">{stats.normales}</span>
                </div>
                
                <div className="flex items-center gap-1.5 bg-blue-50 px-2 py-1 rounded">
                  <FileText size={14} className="text-blue-600" />
                  <span className="text-xs font-medium text-blue-700">{stats.total}</span>
                </div>
              </div>
            </div>

            {/* Lado derecho: Botones de acción */}
            <div className="flex items-center gap-2">
              {onRefrescar && (
                <button
                  onClick={onRefrescar}
                  className="bg-gray-100 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-200 transition-colors flex items-center gap-1.5 text-sm font-medium"
                >
                  <RefreshCw size={14} />
                  Actualizar
                </button>
              )}
              <button
                onClick={handleCrearSolicitud}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-md hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1.5 text-sm font-semibold"
              >
                <Plus size={16} />
                Nueva Solicitud
              </button>
            </div>
          </div>

          {/* Segunda fila: Filtros, buscador y vista */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Filtros de estado - Lado izquierdo */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => cambiarFiltros({disponibilidad: 'todos'})}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  filtros.disponibilidad === 'todos' 
                    ? 'bg-gray-800 text-white shadow-sm' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todas ({solicitudes.length})
              </button>

              <button
                onClick={() => cambiarFiltros({disponibilidad: 'disponible_pasar_precio'})}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  filtros.disponibilidad === 'disponible_pasar_precio' 
                    ? 'bg-gray-800 text-white shadow-sm' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Disponibles y precio ({solicitudesDisponiblesYPasarPrecio.length})
              </button>
              
              <button
                onClick={() => cambiarFiltros({disponibilidad: 'disponibles'})}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  filtros.disponibilidad === 'disponibles' 
                    ? 'bg-green-600 text-white shadow-sm' 
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                <CheckSquare size={14} className="inline mr-1" />
                Disponibles ({solicitudesDisponibles.length})
              </button>
              
              <button
                onClick={() => cambiarFiltros({disponibilidad: 'a_pasar_precio'})}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  filtros.disponibilidad === 'a_pasar_precio' 
                    ? 'bg-orange-600 text-white shadow-sm' 
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                }`}
              >
                <DollarSign size={14} className="inline mr-1" />
                A Pasar Precio ({solicitudesAPasarPrecio.length})
              </button>
              
              {/* Esperando */}
              <button
                onClick={() => cambiarFiltros({disponibilidad: 'esperando'})}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  filtros.disponibilidad === 'esperando' 
                    ? 'bg-blue-600 text-white shadow-sm' 
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                <Hourglass size={14} className="inline mr-1" />
                Esperando ({solicitudesEsperando.length})
              </button>

              {/* A pagar */}
              <button
                onClick={() => cambiarFiltros({disponibilidad: 'a_pagar'})}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  filtros.disponibilidad === 'a_pagar' 
                    ? 'bg-purple-600 text-white shadow-sm' 
                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                }`}
              >
                <Hourglass size={14} className="inline mr-1" />
                A pagar ({solicitudesAPagar.length})
              </button>


            </div>

            {/* Controles de la derecha - Buscador y Vista */}
            <div className="flex items-center gap-3">
              {/* Buscador compacto */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Buscar..."
                  className="w-64 pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  value={filtros.busqueda}
                  onChange={(e) => cambiarFiltros({busqueda: e.target.value})}
                />
              </div>

              {/* Divisor sutil */}
              <div className="h-6 w-px bg-gray-300"></div>

              {/* Selector de vista compacto */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Vista:</span>
                <div className="bg-gray-100 p-0.5 rounded-md flex">
                  <button
                    onClick={() => cambiarVista('cards')}
                    className={`p-1.5 rounded transition-all ${
                      vista === 'cards' 
                        ? 'bg-white shadow-sm text-blue-600' 
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                    title="Vista de tarjetas"
                  >
                    <Grid3X3 size={16} />
                  </button>
                  
                  <button
                    onClick={() => cambiarVista('list')}
                    className={`p-1.5 rounded transition-all ${
                      vista === 'list' 
                        ? 'bg-white shadow-sm text-blue-600' 
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                    title="Vista de lista"
                  >
                    <List size={16} />
                  </button>
                  
                  <button
                    onClick={() => cambiarVista('compact')}
                    className={`p-1.5 rounded transition-all ${
                      vista === 'compact' 
                        ? 'bg-white shadow-sm text-blue-600' 
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                    title="Vista compacta"
                  >
                    <BarChart3 size={16} />
                  </button>
                  
                  <button
                    onClick={() => cambiarVista('kanban')}
                    className={`p-1.5 rounded transition-all ${
                      vista === 'kanban' 
                        ? 'bg-white shadow-sm text-blue-600' 
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                    title="Vista kanban"
                  >
                    <Clock size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <NuevaSolicitud
        isOpen={modalNuevaSolicitudAbierto}
        onClose={handleCerrarSolicitud} 
        isLoading={creandoSolicitud}
      />

    
      {/* Contenido según la vista */}
      {solicitudesFiltradas.length === 0 ? (
        // Estado vacío
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            {solicitudes.length === 0 ? (
              <Archive size={40} className="text-gray-400" />
            ) : (
              <Search size={40} className="text-gray-400" />
            )}
          </div>
          
          <h3 className="text-xl font-semibold text-gray-900 mb-3">
            {solicitudes.length === 0 
              ? ('No hay solicitudes en el sistema')
              : 'No se encontraron resultados'
            }
          </h3>
          
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {solicitudes.length === 0 
              ? ('Aún no se han creado solicitudes en el sistema. Puedes crear la primera solicitud.')
              : 'Intenta ajustar los filtros de búsqueda para encontrar lo que buscas.'
            }
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {solicitudes.length === 0 ? (
              <>
                {onRefrescar && (
                  <button
                    onClick={onRefrescar}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
                  >
                    <RefreshCw size={18} />
                    Actualizar
                  </button>
                )}
                <button
                    onClick={handleCrearSolicitud}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium"
                  >
                    <Plus size={18} />
                    Crear Primera Solicitud
                  </button>
              </>
            ) : (
              <button
                onClick={limpiarFiltros}
                className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Limpiar Filtros
              </button>
            )}
          </div>
        </div>
      ) : (
        // Mostrar solicitudes según la vista seleccionada
        <>
          {vista === 'cards' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {solicitudesFiltradas.map((solicitud) => {
                return <CardSolicitud solicitud={solicitud}/>
              })}
            </div>
          )}
          
          {vista === 'list' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Solicitud</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Materia</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Fecha Entrega</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Prioridad</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Disponibilidad</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Estado</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {solicitudesFiltradas.map((solicitud, index) => {
                      const infoServicio = obtenerInfoServicio(solicitud.servicio);
                      return (
                        <tr key={solicitud.id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg bg-gradient-to-br ${infoServicio.color} text-white`}>
                                {React.cloneElement(infoServicio.icon, { size: 16 })}
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">#{solicitud.id}</div>
                                <div className="text-sm text-gray-600">{infoServicio.label}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="font-medium text-gray-900">{solicitud.materia}</div>
                            <div className="text-sm text-gray-600 line-clamp-1">{solicitud.resumen}</div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="text-gray-900">{formatearFechaCompleta(solicitud.fechaentrega)}</div>
                          </td>
                          <td className="py-4 px-6">
                            {obtenerBadgePrioridad(solicitud.fechaentrega)}
                          </td>
                          <td className="py-4 px-6">
                            {obtenerBadgeDisponibilidad(solicitud)}
                          </td>
                          <td className="py-4 px-6">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {solicitud.Estado}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              <button
                                //onClick={() => onVerDetalles?.(solicitud)}
                                className="bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors text-sm flex items-center gap-1"
                              >
                                <Eye size={12} />
                                Ver
                              </button>
                              <button
                                  //onClick={() => onEditarSolicitud(solicitud)}
                                  className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-200 transition-colors text-sm flex items-center gap-1"
                                >
                                  <Edit size={12} />
                                  Editar
                                </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {vista === 'compact' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {solicitudesFiltradas.map((solicitud) => {
                return <CardSolicitud solicitud={solicitud}/>
              })}
            </div>
          )}

          {vista === 'kanban' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(50vh-90px)]">
            {/* Columna: Menos de 4 horas */}
            <div className="bg-red-50 rounded-xl p-4 border-2 border-red-200 flex flex-col h-full">
              <div className="flex items-center gap-2 mb-4 flex-shrink-0">
                <div className="bg-red-500 text-white p-2 rounded-lg">
                  <AlertCircle size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-red-800">Recién Publicado (&lt; 4h)</h3>
                  <p className="text-sm text-red-600">
                    {solicitudesFiltradas.filter(s => {
                      const { totalHoras } = calcularTiempoPublicado(s.fechasistema);
                      return totalHoras < 4;
                    }).length} solicitudes
                  </p>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                {solicitudesFiltradas
                  .filter(s => {
                    const { totalHoras } = calcularTiempoPublicado(s.fechasistema);
                    return totalHoras < 4;
                  })
                  .map((solicitud) => 
                    <CardSolicitud solicitud={solicitud}/>
                  )}
                  
                {solicitudesFiltradas.filter(s => {
                  const { totalHoras } = calcularTiempoPublicado(s.fechasistema);
                  return totalHoras < 4;
                }).length === 0 && mensajeColumnaVacia('urgente', '📭', 'No hay solicitudes recién publicadas')}
              </div>
            </div>

            {/* Columna: Entre 4 y 12 horas */}
            <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200 flex flex-col h-full">
              <div className="flex items-center gap-2 mb-4 flex-shrink-0">
                <div className="bg-blue-500 text-white p-2 rounded-lg">
                  <Clock size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-blue-800">Pendiente (4h - 12h)</h3>
                  <p className="text-sm text-blue-600">
                    {solicitudesFiltradas.filter(s => {
                      const { totalHoras } = calcularTiempoPublicado(s.fechasistema);
                      return totalHoras >= 4 && totalHoras < 12;
                    }).length} solicitudes
                  </p>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                {solicitudesFiltradas
                  .filter(s => {
                    const { totalHoras } = calcularTiempoPublicado(s.fechasistema);
                    return totalHoras >= 4 && totalHoras < 12;
                  })
                  .map((solicitud) => 
                    <CardSolicitud solicitud={solicitud}/>
                  )}
                  
                {solicitudesFiltradas.filter(s => {
                  const { totalHoras } = calcularTiempoPublicado(s.fechasistema);
                  return totalHoras >= 4 && totalHoras < 12;
                }).length === 0 && mensajeColumnaVacia('medio', '⏰', 'No hay solicitudes en proceso')}
              </div>
            </div>

            {/* Columna: Más de 12 horas */}
            <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200 flex flex-col h-full">
              <div className="flex items-center gap-2 mb-4 flex-shrink-0">
                <div className="bg-gray-500 text-white p-2 rounded-lg">
                  <CheckCircle size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800">Prioridad Maxima (&gt; 12h)</h3>
                  <p className="text-sm text-gray-600">
                    {solicitudesFiltradas.filter(s => {
                      const { totalHoras } = calcularTiempoPublicado(s.fechasistema);
                      return totalHoras >= 12;
                    }).length} solicitudes
                  </p>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                {solicitudesFiltradas
                  .filter(s => {
                    const { totalHoras } = calcularTiempoPublicado(s.fechasistema);
                    return totalHoras >= 12;
                  })
                  .map((solicitud) => 
                    <CardSolicitud solicitud={solicitud}/>
                  )}
                  
                {solicitudesFiltradas.filter(s => {
                  const { totalHoras } = calcularTiempoPublicado(s.fechasistema);
                  return totalHoras >= 12;
                }).length === 0 && mensajeColumnaVacia('normal', '✅', 'No hay solicitudes establecidas')}
              </div>
            </div>
          </div>
          )}

        </>
      )}

      {/* Click fuera para cerrar menús */}
      {menuAbierto && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setMenuAbierto(null)}
        />
      )}
    </div>
  );
  
};

export default DashboardSolicitudes;